/**
 * TasksUsageInline — Indicador compacto de uso de Tarefas + Espaço de Arquivos
 *
 * Versão minimalista do TasksUsageCard, projetada para viver inline no header
 * das páginas (acima dos botões de ação). Ocupa apenas a altura de uma linha
 * de texto (~22px) e exibe dois mini-donuts SVG com tooltip detalhado on hover.
 *
 * Inspiração visual: Linear, Vercel, Raycast (chips minimalistas, sem card).
 *
 * @version 1.0.0
 * RELEASE: 2026-04-20 v1.0.0 — Criação inicial para reduzir invasividade visual
 */

import { useNavigate } from 'react-router-dom';
import { ArrowUpRight, ListTodo, FolderOpen } from 'lucide-react';

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

import { useTasksUsage, type TasksUsage } from '@/hooks/useTasksUsage';
import { useAuth } from '@/hooks/useAuth';

/* ────────────────────────────────────────────────────────────────────────── */
/*  Helpers                                                                   */
/* ────────────────────────────────────────────────────────────────────────── */

/**
 * Formata MB → "MB" ou "GB" amigável.
 * v1.1.0 — usa 1 casa decimal para valores < 100 MB para refletir
 * variações pequenas (ex.: 1.6 MB ao invés de 2 MB).
 */
function formatStorage(mb: number): string {
  if (mb >= 1024) return `${(mb / 1024).toFixed(1)}GB`;
  if (mb < 100) return `${mb.toFixed(1)}MB`;
  return `${Math.round(mb)}MB`;
}

/**
 * Resolve o tom (cor) do donut/texto baseado no % consumido.
 * Usa tokens semânticos do design system.
 */
function getTone(pct: number): { stroke: string; text: string } {
  if (pct >= 95) return { stroke: 'stroke-destructive', text: 'text-destructive' };
  if (pct >= 80) return { stroke: 'stroke-amber-500', text: 'text-amber-600 dark:text-amber-400' };
  return { stroke: 'stroke-emerald-500', text: 'text-emerald-600 dark:text-emerald-400' };
}

/** Verifica se é admin (controla a seta de upgrade) */
function isAdminRole(roles: { role: string; tenant_id: string | null }[], tenantId?: string): boolean {
  if (!tenantId) return false;
  return roles.some(
    (r) =>
      (r.role === 'tenant_admin' && r.tenant_id === tenantId) ||
      (r.role === 'root_admin' && r.tenant_id === null),
  );
}

/* ────────────────────────────────────────────────────────────────────────── */
/*  Donut SVG (14px) — arco proporcional ao % usado                           */
/* ────────────────────────────────────────────────────────────────────────── */

interface DonutProps {
  pct: number;
  toneClass: string;
  ariaLabel: string;
}

function Donut({ pct, toneClass, ariaLabel }: DonutProps) {
  const size = 14;
  const stroke = 2.25;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const safePct = Math.min(100, Math.max(0, pct));
  const dashOffset = circumference - (safePct / 100) * circumference;

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      role="img"
      aria-label={ariaLabel}
      className="shrink-0 -rotate-90"
    >
      {/* Trilho */}
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        strokeWidth={stroke}
        className="stroke-muted-foreground/20"
      />
      {/* Progresso */}
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        strokeWidth={stroke}
        strokeLinecap="round"
        strokeDasharray={circumference}
        strokeDashoffset={dashOffset}
        className={cn('transition-[stroke-dashoffset] duration-500', toneClass)}
      />
    </svg>
  );
}

/* ────────────────────────────────────────────────────────────────────────── */
/*  Chip individual (donut + label + tooltip)                                 */
/* ────────────────────────────────────────────────────────────────────────── */

interface ChipProps {
  icon: React.ReactNode;
  pct: number;
  primaryLabel: string;
  ariaLabel: string;
  tooltipTitle: string;
  tooltipBody: React.ReactNode;
}

function UsageChip({ icon, pct, primaryLabel, ariaLabel, tooltipTitle, tooltipBody }: ChipProps) {
  const tone = getTone(pct);

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div
          className={cn(
            'inline-flex items-center gap-1.5 rounded-md border border-border/40',
            'px-2 py-0.5 h-[22px] text-xs tabular-nums',
            'bg-background/60 hover:bg-muted/50 transition-colors cursor-default',
          )}
        >
          <Donut pct={pct} toneClass={tone.stroke} ariaLabel={ariaLabel} />
          <span className="text-muted-foreground/70 hidden sm:inline">{icon}</span>
          <span className={cn('font-medium hidden sm:inline', tone.text)}>{primaryLabel}</span>
        </div>
      </TooltipTrigger>
      <TooltipContent side="bottom" className="max-w-[260px] text-xs">
        <div className="font-semibold mb-1">{tooltipTitle}</div>
        {tooltipBody}
      </TooltipContent>
    </Tooltip>
  );
}

/* ────────────────────────────────────────────────────────────────────────── */
/*  Componente principal                                                      */
/* ────────────────────────────────────────────────────────────────────────── */

export interface TasksUsageInlineProps {
  /** Classe extra para o wrapper externo */
  className?: string;
  /** Esconde a seta de upgrade mesmo para admin */
  hideUpgradeButton?: boolean;
}

export function TasksUsageInline({ className, hideUpgradeButton = false }: TasksUsageInlineProps) {
  const navigate = useNavigate();
  const { currentTenant, roles } = useAuth();
  const { data: usage, isLoading } = useTasksUsage();

  const isAdmin = isAdminRole(roles, currentTenant?.id);

  // ── Estado de carregamento — dois mini-skeletons inline ────────────────
  if (isLoading || !usage) {
    return (
      <div className={cn('inline-flex items-center gap-1.5', className)}>
        <Skeleton className="h-[22px] w-20 rounded-md" />
        <Skeleton className="h-[22px] w-20 rounded-md" />
      </div>
    );
  }

  const u: TasksUsage = usage;
  const packQty = u.active_pack_quantity ?? 0;
  const storageExtras = u.active_storage_addons_qty ?? 0;

  return (
    <TooltipProvider delayDuration={150}>
      <div className={cn('inline-flex items-center gap-1.5', className)}>
        {/* ── Chip: Tarefas no mês ─────────────────────────────────────── */}
        <UsageChip
          icon={<ListTodo className="h-3 w-3" />}
          pct={u.tasks_pct_used}
          primaryLabel={`${u.tasks_created_month.toLocaleString('pt-BR')}/${u.tasks_limit.toLocaleString('pt-BR')}`}
          ariaLabel={`${u.tasks_created_month} de ${u.tasks_limit} tarefas usadas neste mês`}
          tooltipTitle="Tarefas no mês"
          tooltipBody={
            <div className="space-y-0.5 text-muted-foreground">
              <div>
                <span className="text-foreground font-medium">{u.tasks_pct_used.toFixed(1)}%</span> consumido
              </div>
              <div>
                Restam{' '}
                <span className="text-foreground font-medium">
                  {u.tasks_remaining.toLocaleString('pt-BR')}
                </span>{' '}
                tarefas
              </div>
              <div>
                Plano:{' '}
                <span className="text-foreground">
                  {packQty > 0 ? `+${packQty} pacote${packQty > 1 ? 's' : ''} de 1k` : 'Base'}
                </span>
              </div>
            </div>
          }
        />

        {/* ── Chip: Espaço de Arquivos ─────────────────────────────────── */}
        <UsageChip
          icon={<FolderOpen className="h-3 w-3" />}
          pct={u.storage_pct_used}
          primaryLabel={`${formatStorage(u.storage_used_mb)}/${formatStorage(u.storage_limit_mb)}`}
          ariaLabel={`${formatStorage(u.storage_used_mb)} de ${formatStorage(u.storage_limit_mb)} de armazenamento usados`}
          tooltipTitle="Espaço de Arquivos"
          tooltipBody={
            <div className="space-y-0.5 text-muted-foreground">
              <div>
                <span className="text-foreground font-medium">{u.storage_pct_used.toFixed(1)}%</span> consumido
              </div>
              <div>
                Disponível:{' '}
                <span className="text-foreground font-medium">
                  {formatStorage(u.storage_remaining_mb)}
                </span>
              </div>
              {storageExtras > 0 && (
                <div>
                  Extras:{' '}
                  <span className="text-foreground">
                    +{storageExtras * 10} GB ({storageExtras}× pacote)
                  </span>
                </div>
              )}
            </div>
          }
        />

        {/* ── Atalho de upgrade (apenas admin) ─────────────────────────── */}
        {isAdmin && !hideUpgradeButton && (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={() => navigate('/tenant/plans?focus=tasks')}
                className={cn(
                  'inline-flex items-center justify-center h-[22px] w-[22px] rounded-md',
                  'border border-border/40 text-muted-foreground hover:text-primary',
                  'hover:bg-primary/10 hover:border-primary/30 transition-colors',
                )}
                aria-label="Aumentar capacidade de tarefas"
              >
                <ArrowUpRight className="h-3 w-3" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="text-xs">
              Aumentar capacidade
            </TooltipContent>
          </Tooltip>
        )}
      </div>
    </TooltipProvider>
  );
}
